import { useEffect, useState } from "react";
import products from '../demo/products.json'
import { useParams } from "react-router-dom";
import './../styles/SingleProduct.scss'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faHeart as faHeartSolid } from "@fortawesome/free-solid-svg-icons";
import { faHeart as faHeartRegular } from "@fortawesome/free-regular-svg-icons";
import Tabs from "../components/Tabs/Tabs";
import useProductStore from "../store/productStore";

// Import Swiper styles and modules
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import useFavouriteStore from "../store/favouriteStore";
import useUserStore from "../store/userStore";
import { createFavourite } from "../types/Favourite";
import { useLoadingStore } from "../store/loadingStore";
import useToastStore from "../store/toastStore";
import { createCartItemDTO } from "../types/Cart";
import { Product } from "../types/product";
import useCartStore from "../store/cartStore";

const SingleProduct = () => {
    const { code } = useParams();
    const {selectedProduct, getProductBySku} = useProductStore();
    const [quantities, setQuantities] = useState<{ [sku: string]: number }>({});
    const [isMobile, setIsMobile] = useState(false);
    const {addToCart} = useCartStore();
    const { favourites, addFavourite, removeFavourite, getUserFavourites } = useFavouriteStore();
    const { isAuthenticated,user } = useUserStore();
    const isFavourite = favourites.some(fav => fav.productId === selectedProduct?.id);
    const {setLoading} = useLoadingStore();
    const {showToast} = useToastStore();


    useEffect(() => {
      const fetchInfo = async () => {
        try {
          await Promise.all([
            getProductBySku(code),
            getUserFavourites()
          ]);
        } catch (error) {
          console.error("Error fetching product data:", error);
        }
      }
      fetchInfo();
        // Check if window is defined (for SSR)
        if (typeof window !== 'undefined') {
            const handleResize = () => {
                setIsMobile(window.innerWidth <= 1100);
            };
            
            // Set initial value
            handleResize();
            
            // Add event listener
            window.addEventListener('resize', handleResize);
            
            // Clean up
            return () => window.removeEventListener('resize', handleResize);
        }
    }, [code,user,getProductBySku,getUserFavourites]);

    

    const handleFavouriteToggle = async () => {
      if (!selectedProduct) return;

      try {
        setLoading(true);
          if (isFavourite) {
              const favouriteItem = favourites.find(fav => fav.sku === selectedProduct.sku);
              if (favouriteItem) {
                  await removeFavourite(favouriteItem.id);
                  showToast('Product removed from favourites!','success')
              }
          } else {
              const newFav = createFavourite();
              newFav.productId =  selectedProduct.id
              newFav.userId = user.id;
              await addFavourite(newFav);
              showToast('Product added to favourites!','success')
          }

          await getUserFavourites();
          setLoading(false);
      } catch (error) {
          console.error("Error updating favourites:", error);
          setLoading(false);
          showToast('Error updating favourites!','error')
      }
  };

    const handleQuantityChange = (sku: string, delta: number) => {
        setQuantities((prev) => ({
            ...prev,
            [sku]: Math.max((prev[sku] || 0) + delta, 0),
        }));
    };
    const handleAddToCart = async (product: Product) => {
      const cartItem = createCartItemDTO();
      cartItem.productId = product?.id;
      cartItem.price = product?.b2bPrice;
      cartItem.name = product?.name;
      cartItem.image = product.banner;
      cartItem.quantity= quantities[product.sku] || 1;
      setLoading(true);
      try{
        await addToCart(cartItem);
        setQuantities((prev) => ({
          ...prev,
          [product.sku]: 0,
        }));
        showToast('Added product to cart!','success')
      }
      catch(error){
        showToast('Error product to cart!','error')
      }
      setLoading(false);
     
    };

    if (!selectedProduct) {
        return <div>Product not found</div>;
    }

    return (
        <div className="single-product">
            {/* Mobile View */}
            {isMobile ? (
                <div className="mobile-view">
                    <div className="slider-container">
                        <Swiper
                            modules={[Navigation, Pagination]}
                            spaceBetween={0}
                            slidesPerView={1}
                            navigation
                            pagination={{ clickable: true }}
                        >
                
                            <SwiperSlide>
                                <img 
                                    src={selectedProduct?.banner} 
                                    alt={selectedProduct?.name} 
                                    className="slider-image"
                                />
                            </SwiperSlide>
                            
                           
                            {selectedProduct?.images.map((image, index) => (
                                <SwiperSlide key={index}>
                                    <img 
                                        src={image} 
                                        alt={`Product Image ${index + 1}`} 
                                        className="slider-image"
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>

                    <div className="product-details">
                        <h1>{selectedProduct?.name}</h1>
                        <div className="sku-row">
                            <div className="sku">SKU: {selectedProduct?.sku}</div>
                            {selectedProduct?.salesPercentage != null && (
                                <div className="percent">-{selectedProduct?.salesPercentage}%</div>
                            )}
                        </div>
                        <div className="prices">
                            {selectedProduct?.inContract ? (
                                <>
                                    <span className="b2b-price">{selectedProduct?.b2bPrice.toFixed(2)}€</span>
                                    <span className="b2c-price struck">{selectedProduct?.b2cPrice.toFixed(2)}€</span>
                                </>
                            ) : (
                                <span className="b2c-price">{selectedProduct?.b2cPrice.toFixed(2)}€</span>
                            )}
                        </div>
                        <div className={`status ${selectedProduct?.status?.replace(/\s+/g, '-').toLowerCase()}`}>
                            {selectedProduct?.status}
                        </div>

                        <div className="quantity-controls mobile-quantity">
                            <div className="quant">Quantity</div>
                            <div className="quantity-buttons">
                                <div className="quantity-buttons-mob-wrap">
                                  <button className="quanBtn" onClick={() => handleQuantityChange(selectedProduct?.sku, -1)}>
                                      -
                                  </button>
                                  <span>{quantities[selectedProduct?.sku] || 0}</span>
                                  <button className="quanBtn" onClick={() => handleQuantityChange(selectedProduct?.sku, 1)}>
                                      +
                                  </button>
                                </div>
                            </div>
                        </div>

                        <button
                            className="add-to-cart mobile-add-to-cart"
                            onClick={() => handleAddToCart(selectedProduct)}
                            disabled={!isAuthenticated || !selectedProduct?.inContract}
                        >
                            Add to Cart
                        </button>

                        <Tabs />
                    </div>
                </div>
            ) : (
                /* Desktop View  */
                <div className="desktop-view">
                    <div className="col-1">
                        <div className="banner">
                            <img src={selectedProduct?.banner} alt={selectedProduct?.name} />
                        </div>
                        <div className="images-list">
                            <FontAwesomeIcon icon={faChevronUp}/>
                            {selectedProduct?.images.map((image, index) => (
                                <img key={index} src={image} alt={`Product Image ${index + 1}`} />
                            ))}
                            <FontAwesomeIcon icon={faChevronDown}/>
                        </div>
                    </div>

                    <div className="col-2">
                        <h1>{selectedProduct?.name}</h1>
                        <div className="sku-row">
                            <div className="sku">SKU: {selectedProduct?.sku}</div>
                            {selectedProduct?.salesPercentage > 0  && (
                                <div className="percent">-{selectedProduct?.salesPercentage}%</div>
                            )}
                        </div>
                        <div className="prices">
                            {selectedProduct?.inContract ? (
                                <>
                                    <span className="b2b-price">{selectedProduct?.b2bPrice.toFixed(2)}€</span>
                                    <span className="b2c-price struck">{selectedProduct?.b2cPrice.toFixed(2)}€</span>
                                </>
                            ) : (
                                <span className="b2c-price">{selectedProduct?.b2cPrice.toFixed(2)}€</span>
                            )}
                        </div>
                        <div className={`status ${selectedProduct?.status?.replace(/\s+/g, '-').toLowerCase()}`}>
                            {selectedProduct?.status}
                        </div>
                        <div className="cartRow">
                        <div className="cartCol">
                            <div className="quant">Quantity</div>
                            <div className="addCart">
                                <button
                                    className="add-to-cart"
                                    onClick={() => handleAddToCart(selectedProduct)}
                                    disabled={!isAuthenticated || !selectedProduct?.inContract}
                                >
                                    Add to Cart
                                </button>
                                <div className="quantity-controls">
                                    <button className="quanBtn" onClick={() => handleQuantityChange(selectedProduct?.sku, -1)}>
                                        -
                                    </button>
                                    <span>{quantities[selectedProduct?.sku] || 0}</span>
                                    <button className="quanBtn" onClick={() => handleQuantityChange(selectedProduct?.sku, 1)}>
                                        +
                                    </button>
                                </div>
                                
                            </div>
                            </div>
                            {isAuthenticated &&<div className="favourites">
                                    <button 
                                        className={`favourite-btn ${isFavourite ? 'active' : ''}`}
                                        onClick={handleFavouriteToggle}
                                        disabled={!isAuthenticated}
                                        title={isAuthenticated ? 
                                            (isFavourite ? "Remove from favourites" : "Add to favourites") : 
                                            "Please login to use favourites"}
                                    >
                                        <FontAwesomeIcon 
                                            icon={isFavourite ? faHeartSolid : faHeartRegular} 
                                            className="favourite-icon"
                                        />
                                    </button>
                                </div>
                              }
                       
                        </div>
                      
                        <Tabs />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SingleProduct;